import React, { useState, useEffect, useRef } from 'react';
import '../Css/AdminProject.css';
import { API_URL } from '../utils/Function';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faProjectDiagram,
  faPlus,
  faEdit,
  faTrash,
  faSave,
  faTimes,
  faInfoCircle,
  faHeading,
  faLink,
  faAlignLeft,
  faFileAlt,
  faGlobe,
  faCogs,
  faPalette,
  faCalendar,
  faFolder,
  faImages,
  faImage,
  faPhotoVideo,
  faEye,
  faExclamationTriangle,
  faFolderOpen,
  faChevronLeft,
  faChevronRight,
  faPause,
  faPlay,
  faBuilding,
  faRocket,
  faListAlt,
  faUsers,
  faCalendarAlt,
  faCheckCircle,
  faSpinner,
  faPauseCircle,
  faSortNumericDown // Added Icon
} from '@fortawesome/free-solid-svg-icons';

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [featuredImagePreview, setFeaturedImagePreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [autoSlideEnabled, setAutoSlideEnabled] = useState({});
  const slideIntervals = useRef({});

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    short_description: '',
    category_id: '',
    client_name: '',
    project_url: '',
    technologies: '',
    card_color: '#667eea',
    status: 'in-progress',
    start_date: '',
    end_date: '',
    created_by: '',
    display_order: 0, // Added display_order
    featured_image: null,
    gallery_images: []
  });

  const statusOptions = [
    { value: 'in-progress', label: 'In Progress', icon: faSpinner, color: '#f59e0b' },
    { value: 'completed', label: 'Completed', icon: faCheckCircle, color: '#10b981' },
    { value: 'on-hold', label: 'On Hold', icon: faPauseCircle, color: '#6b7280' },
    { value: 'planned', label: 'Planned', icon: faCalendarAlt, color: '#3b82f6' }
  ];

  // Safe technologies parsing function
  const getTechnologiesArray = (technologies) => {
    if (!technologies) return [];
    
    try {
      if (Array.isArray(technologies)) return technologies;
      if (typeof technologies === 'string') {
        if (technologies.trim().startsWith('[')) {
          return JSON.parse(technologies);
        } else {
          return technologies.split(',').map(tech => tech.trim()).filter(tech => tech);
        }
      }
      return [];
    } catch (error) {
      console.error('Error parsing technologies:', error);
      if (typeof technologies === 'string') {
        return technologies.split(',').map(tech => tech.trim()).filter(tech => tech);
      }
      return [];
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchCategories();
    fetchUsers();
    return () => {
      Object.values(slideIntervals.current).forEach(interval => {
        if (interval) clearInterval(interval);
      });
    };
  }, []);

  useEffect(() => {
    projects.forEach(project => {
      const images = getProjectImages(project);
      if (images.length > 1 && autoSlideEnabled[project.id]) {
        startAutoSlide(project.id, images.length);
      } else {
        stopAutoSlide(project.id);
      }
    });

    return () => {
      projects.forEach(project => {
        stopAutoSlide(project.id);
      });
    };
  }, [projects, autoSlideEnabled]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      // Explicitly ask for display_order sorting
      const response = await fetch(`${API_URL}/project.php/get?orderBy=display_order&orderDir=ASC`);
      const data = await response.json();
      if (data.success) {
        setProjects(data.projects);
        const initialIndexes = {};
        const initialAutoSlide = {};
        data.projects.forEach(project => {
          initialIndexes[project.id] = 0;
          initialAutoSlide[project.id] = true;
        });
        setCurrentImageIndex(initialIndexes);
        setAutoSlideEnabled(initialAutoSlide);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Error fetching projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/category.php/get`);
      const data = await response.json();
      if (data.success) {
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/user.php/get`);
      const data = await response.json();
      if (data.success) {
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const startAutoSlide = (projectId, totalImages) => {
    if (slideIntervals.current[projectId]) {
      clearInterval(slideIntervals.current[projectId]);
    }

    slideIntervals.current[projectId] = setInterval(() => {
      setCurrentImageIndex(prev => ({
        ...prev,
        [projectId]: (prev[projectId] + 1) % totalImages
      }));
    }, 4000);
  };

  const stopAutoSlide = (projectId) => {
    if (slideIntervals.current[projectId]) {
      clearInterval(slideIntervals.current[projectId]);
      slideIntervals.current[projectId] = null;
    }
  };

  const toggleAutoSlide = (projectId) => {
    setAutoSlideEnabled(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  const nextImage = (projectId, totalImages) => {
    const wasAutoSlideEnabled = autoSlideEnabled[projectId];
    if (wasAutoSlideEnabled) {
      setAutoSlideEnabled(prev => ({ ...prev, [projectId]: false }));
    }
    setCurrentImageIndex(prev => ({
      ...prev,
      [projectId]: (prev[projectId] + 1) % totalImages
    }));
    if (wasAutoSlideEnabled) {
      setTimeout(() => {
        setAutoSlideEnabled(prev => ({ ...prev, [projectId]: true }));
      }, 10000);
    }
  };

  const prevImage = (projectId, totalImages) => {
    const wasAutoSlideEnabled = autoSlideEnabled[projectId];
    if (wasAutoSlideEnabled) {
      setAutoSlideEnabled(prev => ({ ...prev, [projectId]: false }));
    }
    setCurrentImageIndex(prev => ({
      ...prev,
      [projectId]: (prev[projectId] - 1 + totalImages) % totalImages
    }));
    if (wasAutoSlideEnabled) {
      setTimeout(() => {
        setAutoSlideEnabled(prev => ({ ...prev, [projectId]: true }));
      }, 10000);
    }
  };

  const goToImage = (projectId, index) => {
    const wasAutoSlideEnabled = autoSlideEnabled[projectId];
    if (wasAutoSlideEnabled) {
      setAutoSlideEnabled(prev => ({ ...prev, [projectId]: false }));
    }
    setCurrentImageIndex(prev => ({ ...prev, [projectId]: index }));
    if (wasAutoSlideEnabled) {
      setTimeout(() => {
        setAutoSlideEnabled(prev => ({ ...prev, [projectId]: true }));
      }, 10000);
    }
  };

  const getProjectImages = (project) => {
    const images = [];
    if (project.gallery_images_urls) {
      let galleryUrls = project.gallery_images_urls;
      if (typeof galleryUrls === 'string') {
        try {
          galleryUrls = JSON.parse(galleryUrls);
        } catch (e) {
          console.error('Error parsing gallery images:', e);
          galleryUrls = [galleryUrls];
        }
      }
      if (Array.isArray(galleryUrls)) {
        galleryUrls.forEach(url => {
          if (url && url.trim() !== '') {
            images.push({ url: url, type: 'gallery' });
          }
        });
      }
    }
    if (project.featured_image_url && images.length === 0) {
      images.push({ url: project.featured_image_url, type: 'featured' });
    }
    return images;
  };

  const handleOpenModal = (project = null) => {
    if (project) {
      setEditMode(true);
      setSelectedProject(project);
      
      let existingGallery = [];
      let existingFeaturedImage = null;

      if (project.gallery_images) {
        try {
          existingGallery = JSON.parse(project.gallery_images);
        } catch (e) {
          console.error('Error parsing gallery images:', e);
        }
      }

      if (project.featured_image_url) {
        existingFeaturedImage = project.featured_image_url;
      }

      let technologiesDisplay = '';
      if (project.technologies) {
        const techArray = getTechnologiesArray(project.technologies);
        technologiesDisplay = techArray.join(', ');
      }

      setFormData({
        title: project.title || '',
        slug: project.slug || '',
        description: project.description || '',
        short_description: project.short_description || '',
        category_id: project.category_id || '',
        client_name: project.client_name || '',
        project_url: project.project_url || '',
        technologies: technologiesDisplay,
        card_color: project.card_color || '#667eea',
        status: project.status || 'in-progress',
        start_date: project.start_date || '',
        end_date: project.end_date || '',
        created_by: project.created_by || '',
        display_order: project.display_order || 0, // Set existing order
        featured_image: null,
        gallery_images: []
      });

      const previews = existingGallery.map(filename => ({
        name: filename,
        url: `${API_URL}/uploads/projects/${filename}`,
        isExisting: true
      }));
      setGalleryPreviews(previews);
      setFeaturedImagePreview(existingFeaturedImage);

    } else {
      setEditMode(false);
      setSelectedProject(null);
      setFormData({
        title: '',
        slug: '',
        description: '',
        short_description: '',
        category_id: '',
        client_name: '',
        project_url: '',
        technologies: '',
        card_color: '#667eea',
        status: 'in-progress',
        start_date: '',
        end_date: '',
        created_by: '',
        display_order: 0, // Reset order
        featured_image: null,
        gallery_images: []
      });
      setGalleryPreviews([]);
      setFeaturedImagePreview(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedProject(null);
    setGalleryPreviews([]);
    setFeaturedImagePreview(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      if (name === 'gallery_images') {
        const newFiles = Array.from(files);
        setFormData(prev => ({ 
          ...prev, 
          gallery_images: [...prev.gallery_images, ...newFiles] 
        }));
        
        newFiles.forEach(file => {
          const reader = new FileReader();
          reader.onload = (e) => {
            setGalleryPreviews(prev => [...prev, {
              name: file.name,
              url: e.target.result,
              isExisting: false,
              file: file
            }]);
          };
          reader.readAsDataURL(file);
        });
      } else if (name === 'featured_image') {
        setFormData(prev => ({ ...prev, [name]: files[0] }));
        
        if (files[0]) {
          const reader = new FileReader();
          reader.onload = (e) => {
            setFeaturedImagePreview(e.target.result);
          };
          reader.readAsDataURL(files[0]);
        }
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const removeGalleryImage = (index) => {
    const imageToRemove = galleryPreviews[index];
    const updatedPreviews = [...galleryPreviews];
    updatedPreviews.splice(index, 1);
    setGalleryPreviews(updatedPreviews);
    
    if (!imageToRemove.isExisting) {
      const fileIndex = formData.gallery_images.findIndex(file => 
        file.name === imageToRemove.name
      );
      if (fileIndex > -1) {
        const updatedFiles = [...formData.gallery_images];
        updatedFiles.splice(fileIndex, 1);
        setFormData(prev => ({ ...prev, gallery_images: updatedFiles }));
      }
    }
  };

  const removeFeaturedImage = () => {
    setFormData(prev => ({ ...prev, featured_image: null }));
    setFeaturedImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    
    let technologiesToSend = '';
    if (formData.technologies) {
      const techArray = getTechnologiesArray(formData.technologies);
      technologiesToSend = JSON.stringify(techArray);
    }
    
    Object.keys(formData).forEach(key => {
      if (key !== 'featured_image' && key !== 'gallery_images' && key !== 'technologies') {
        if (formData[key] !== null && formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      }
    });

    if (technologiesToSend) {
      formDataToSend.append('technologies', technologiesToSend);
    }

    if (formData.featured_image) {
      formDataToSend.append('featured_image', formData.featured_image);
    }

    if (formData.gallery_images && formData.gallery_images.length > 0) {
      formData.gallery_images.forEach((file) => {
        formDataToSend.append('gallery_images[]', file);
      });
    }

    if (editMode) {
      const existingImages = galleryPreviews
        .filter(preview => preview.isExisting)
        .map(preview => preview.name);
      
      if (existingImages.length > 0) {
        formDataToSend.append('existing_gallery_images', JSON.stringify(existingImages));
      }
    }

    try {
      let url, method;
      if (editMode) {
        url = `${API_URL}/project.php/put/${selectedProject.id}`;
        method = 'POST'; 
      } else {
        url = `${API_URL}/project.php/post`;
        method = 'POST';
      }

      const response = await fetch(url, {
        method: method,
        body: formDataToSend
      });

      const data = await response.json();
      if (data.success) {
        fetchProjects();
        handleCloseModal();
        if (editMode) {
          toast.success('Project updated successfully!', { className: 'toast-edit' });
        } else {
          toast.success('Project added successfully!', { className: 'toast-add' });
        }
      } else {
        toast.error(data.error || 'An error occurred', { className: 'toast-error' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('An error occurred while submitting the form', { className: 'toast-error' });
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await fetch(`${API_URL}/project.php/delete/${deleteId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        fetchProjects();
        setShowDeleteModal(false);
        setDeleteId(null);
        toast.warning('Project deleted successfully!', { className: 'toast-delete' });
      } else {
        toast.error(data.error || 'An error occurred', { className: 'toast-error' });
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('An error occurred while deleting the project', { className: 'toast-error' });
    }
  };

  const getStatusInfo = (status) => {
    return statusOptions.find(option => option.value === status) || statusOptions[0];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="our-services-container">
      <ToastContainer position="top-right" autoClose={5000} theme="light" />
      
      <div className="our-services-header">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6">
              <h1 className="our-services-heading">
                <FontAwesomeIcon icon={faProjectDiagram} className="me-3" />
                Projects
              </h1>
            </div>
            <div className="col-md-6 text-end">
              <button className="add-service-btn" onClick={() => handleOpenModal()}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add New Project
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mt-5">
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : projects.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <FontAwesomeIcon icon={faFolderOpen} />
            </div>
            <p className="empty-state-text">No projects found. Create your first project!</p>
          </div>
        ) : (
          <div className="row">
            {projects.map((project) => {
              const images = getProjectImages(project);
              const currentIndex = currentImageIndex[project.id] || 0;
              const hasMultipleImages = images.length > 1;
              const isAutoSlideEnabled = autoSlideEnabled[project.id];
              const statusInfo = getStatusInfo(project.status);
              const technologiesArray = getTechnologiesArray(project.technologies);
              
              return (
                <div key={project.id} className="col-lg-4 col-md-6 mb-4">
                  <div className="service-item-card" style={{ borderTop: `4px solid ${project.card_color || '#667eea'}` }}>
                    
                    {/* Display Order Badge - NEW */}
                    <div style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 10 }}>
                        <span className="badge bg-secondary">
                             #{project.display_order}
                        </span>
                    </div>

                    {/* Image Slider */}
                    {images.length > 0 ? (
                      <div className="service-item-image">
                        <img 
                          src={`${API_URL}/${images[currentIndex].url}`} 
                          alt={project.title} 
                        />
                        {hasMultipleImages && (
                          <>
                            <button 
                              className="slider-arrow slider-arrow-prev"
                              onClick={(e) => { e.stopPropagation(); prevImage(project.id, images.length); }}
                            >
                              <FontAwesomeIcon icon={faChevronLeft} />
                            </button>
                            <button 
                              className="slider-arrow slider-arrow-next"
                              onClick={(e) => { e.stopPropagation(); nextImage(project.id, images.length); }}
                            >
                              <FontAwesomeIcon icon={faChevronRight} />
                            </button>
                            <div className="image-counter">
                              <FontAwesomeIcon icon={faImages} className="me-1" />
                              {currentIndex + 1} / {images.length}
                            </div>
                            <button 
                              className="slider-control"
                              onClick={(e) => { e.stopPropagation(); toggleAutoSlide(project.id); }}
                              title={isAutoSlideEnabled ? "Pause auto-slide" : "Play auto-slide"}
                            >
                              <FontAwesomeIcon icon={isAutoSlideEnabled ? faPause : faPlay} />
                            </button>
                            <div className="slider-dots">
                              {images.map((_, index) => (
                                <button
                                  key={index}
                                  className={`dot ${index === currentIndex ? 'active' : ''}`}
                                  onClick={(e) => { e.stopPropagation(); goToImage(project.id, index); }}
                                />
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="service-item-image">
                        <div className="no-image-placeholder">
                          <FontAwesomeIcon icon={faImage} />
                          <span>No Image</span>
                        </div>
                      </div>
                    )}
                    
                    <div className="service-item-body">
                      <h5 className="service-item-title">{project.title}</h5>
                      {project.client_name && (
                        <p className="service-item-tagline">
                          <FontAwesomeIcon icon={faBuilding} className="me-1" />
                          {project.client_name}
                        </p>
                      )}
                      <p className="service-item-description">
                        {project.short_description || project.description?.substring(0, 120) + '...'}
                      </p>
                      
                      {technologiesArray.length > 0 && (
                        <div className="mb-3">
                          <span className="service-item-badge">
                            <FontAwesomeIcon icon={faCogs} className="me-1" />
                            {technologiesArray.slice(0, 3).join(', ')}
                            {technologiesArray.length > 3 && ` +${technologiesArray.length - 3} more`}
                          </span>
                        </div>
                      )}

                      {project.project_url && (
                        <div className="mb-2">
                          <small className="text-muted">
                            <FontAwesomeIcon icon={faGlobe} className="me-1" />
                            <a href={project.project_url} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                              View Project
                            </a>
                          </small>
                        </div>
                      )}

                      {project.category_name && (
                        <div className="mb-2">
                          <small className="text-muted">
                            <FontAwesomeIcon icon={faFolder} className="me-1" />
                            {project.category_name}
                          </small>
                        </div>
                      )}
                    </div>
                    
                    <div className="service-item-status">
                      <div className="status-badges">
                        <span 
                          className="status-badge"
                          style={{ 
                            background: `${statusInfo.color}15`,
                            color: statusInfo.color,
                            borderColor: `${statusInfo.color}30`
                          }}
                        >
                          <FontAwesomeIcon icon={statusInfo.icon} className="me-1" />
                          {statusInfo.label}
                        </span>
                        
                        {(project.start_date || project.end_date) && (
                          <span className="status-badge display-order">
                            <FontAwesomeIcon icon={faCalendar} className="me-1" />
                            {project.start_date ? formatDate(project.start_date) : 'TBD'} - {project.end_date ? formatDate(project.end_date) : 'Ongoing'}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="service-item-footer">
                      <button 
                        className="btn btn-sm btn-outline-primary" 
                        onClick={() => handleOpenModal(project)}
                        title="Edit Project"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button 
                        className="btn btn-sm btn-outline-danger" 
                        onClick={() => handleDeleteClick(project.id)}
                        title="Delete Project"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Project Modal */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FontAwesomeIcon icon={editMode ? faEdit : faPlus} className="me-2" />
                  {editMode ? 'Edit Project' : 'Add New Project'}
                </h5>
                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit} id="projectForm">
                  <div className="row">
                    <div className="col-12 mb-4">
                      <h6 className="text-primary fw-bold mb-3">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                        Basic Information
                      </h6>
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faHeading} className="me-1" />
                        Title <span className="text-danger">*</span>
                      </label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="title" 
                        value={formData.title} 
                        onChange={handleInputChange} 
                        placeholder="Enter project title"
                        required 
                      />
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faLink} className="me-1" />
                        Slug <span className="text-danger">*</span>
                      </label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="slug" 
                        value={formData.slug} 
                        onChange={handleInputChange} 
                        placeholder="project-url-slug"
                        required 
                      />
                    </div>

                    {/* NEW Display Order Input */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faSortNumericDown} className="me-1" />
                        Display Order
                      </label>
                      <input 
                        type="number" 
                        className="form-control" 
                        name="display_order" 
                        value={formData.display_order} 
                        onChange={handleInputChange} 
                        placeholder="0"
                      />
                      <small className="text-muted">Lower numbers appear first</small>
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faFolder} className="me-1" />
                        Category <span className="text-danger">*</span>
                      </label>
                      <select 
                        className="form-select" 
                        name="category_id" 
                        value={formData.category_id} 
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Category</option>
                        {categories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faBuilding} className="me-1" />
                        Client Name
                      </label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="client_name" 
                        value={formData.client_name} 
                        onChange={handleInputChange} 
                        placeholder="Client company name"
                      />
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faGlobe} className="me-1" />
                        Project URL
                      </label>
                      <input 
                        type="url" 
                        className="form-control" 
                        name="project_url" 
                        value={formData.project_url} 
                        onChange={handleInputChange} 
                        placeholder="https://example.com"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faUsers} className="me-1" />
                        Created By <span className="text-danger">*</span>
                      </label>
                      <select 
                        className="form-select" 
                        name="created_by" 
                        value={formData.created_by} 
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select User</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.name || user.email}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faAlignLeft} className="me-1" />
                        Description <span className="text-danger">*</span>
                      </label>
                      <textarea 
                        className="form-control" 
                        name="description" 
                        value={formData.description} 
                        onChange={handleInputChange} 
                        rows="4" 
                        placeholder="Detailed description of the project"
                        required
                      ></textarea>
                    </div>
                    
                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faFileAlt} className="me-1" />
                        Short Description
                      </label>
                      <textarea 
                        className="form-control" 
                        name="short_description" 
                        value={formData.short_description} 
                        onChange={handleInputChange} 
                        rows="2"
                        placeholder="Brief summary for cards and previews"
                      ></textarea>
                    </div>

                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faCogs} className="me-1" />
                        Technologies
                      </label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="technologies" 
                        value={formData.technologies} 
                        onChange={handleInputChange} 
                        placeholder='React, Node.js, MongoDB (comma separated)'
                      />
                    </div>

                    {/* Media Section */}
                    <div className="col-12 mb-4 mt-3">
                      <h6 className="text-primary fw-bold mb-3">
                        <FontAwesomeIcon icon={faImages} className="me-2" />
                        Media & Images
                      </h6>
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faImage} className="me-1" />
                        Featured Image
                      </label>
                      <input 
                        type="file" 
                        className="form-control" 
                        name="featured_image" 
                        onChange={handleInputChange} 
                        accept="image/*"
                      />
                      
                      {featuredImagePreview && (
                        <div className="mt-3 position-relative">
                          <h6 className="mb-2">
                            <FontAwesomeIcon icon={faEye} className="me-2" />
                            Featured Image Preview:
                          </h6>
                          <div className="position-relative d-inline-block">
                            <img 
                              src={`${API_URL}/${featuredImagePreview}`} 
                              alt="Featured preview"
                              className="featured-image-preview"
                            />
                            <button
                              type="button"
                              className="btn btn-danger btn-sm position-absolute top-0 end-0"
                              onClick={removeFeaturedImage}
                              title="Remove image"
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faPhotoVideo} className="me-1" />
                        Gallery Images
                      </label>
                      <input 
                        type="file" 
                        className="form-control" 
                        name="gallery_images" 
                        onChange={handleInputChange} 
                        accept="image/*" 
                        multiple
                      />
                      
                      {galleryPreviews.length > 0 && (
                        <div className="mt-3">
                          <h6 className="mb-2">
                            <FontAwesomeIcon icon={faEye} className="me-2" />
                            Gallery Previews:
                          </h6>
                          <div className="row">
                            {galleryPreviews.map((preview, index) => (
                              <div key={index} className="col-md-4 col-6 mb-2 position-relative">
                                <img 
                                  src={preview.url} 
                                  alt={`Gallery ${index + 1}`}
                                  className="gallery-image-preview"
                                />
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm position-absolute top-0 end-0"
                                  onClick={() => removeGalleryImage(index)}
                                  title="Remove image"
                                >
                                  <FontAwesomeIcon icon={faTimes} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Project Details Section */}
                    <div className="col-12 mb-4 mt-3">
                      <h6 className="text-primary fw-bold mb-3">
                        <FontAwesomeIcon icon={faListAlt} className="me-2" />
                        Project Details
                      </h6>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faPalette} className="me-1" />
                        Card Color
                      </label>
                      <input 
                        type="color" 
                        className="form-control" 
                        name="card_color" 
                        value={formData.card_color} 
                        onChange={handleInputChange}
                        style={{ height: '45px' }}
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faRocket} className="me-1" />
                        Status
                      </label>
                      <select 
                        className="form-select" 
                        name="status" 
                        value={formData.status} 
                        onChange={handleInputChange}
                      >
                        {statusOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                        Start Date
                      </label>
                      <input 
                        type="date" 
                        className="form-control" 
                        name="start_date" 
                        value={formData.start_date} 
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faCalendar} className="me-1" />
                        End Date
                      </label>
                      <input 
                        type="date" 
                        className="form-control" 
                        name="end_date" 
                        value={formData.end_date} 
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  <FontAwesomeIcon icon={faTimes} className="me-1" />
                  Cancel
                </button>
                <button type="submit" form="projectForm" className="btn btn-primary">
                  <FontAwesomeIcon icon={editMode ? faSave : faPlus} className="me-1" />
                  {editMode ? 'Update Project' : 'Add Project'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-danger">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  Confirm Delete
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this project? This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  <FontAwesomeIcon icon={faTimes} className="me-1" />
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleConfirmDelete}>
                  <FontAwesomeIcon icon={faTrash} className="me-1" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}